import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import {
  Backdrop as MuiBackdrop,
  BackdropProps as MuiBackdropProps,
  CircularProgress,
  SxProps,
  Theme,
} from '@mui/material';

const defaultProgressSx: SxProps<Theme> = {
  color: 'common.white',
  zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
};

export interface LoadingBackdropProps extends Omit<MuiBackdropProps, 'children'> {
  /**
   * Whether the loading backdrop is open.
   */
  open: boolean;

  /**
   * Custom content to display in the loading backdrop.
   * If not provided, a CircularProgress will be shown (when showSpinner is true).
   */
  children?: ReactNode;

  /**
   * Whether to show the default loading spinner.
   * @default true
   */
  showSpinner?: boolean;

  /**
   * Size of the circular progress spinner (px).
   * @default 40
   */
  spinnerSize?: number;

  /**
   * Color of the spinner.
   * @default 'inherit'
   */
  spinnerColor?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

  /**
   * Custom styles for the loading backdrop.
   */
  sx?: SxProps<Theme>;

  /**
   * Data test ID for testing.
   */
  'data-testid'?: string;
}

/**
 * Full-screen loading backdrop for blocking UI during async operations.
 *
 * Renders an overlay with a default CircularProgress spinner, or custom content.
 * Z-index is set above the drawer by default so it appears on top of sidebars.
 *
 * @example
 * ```tsx
 * <LoadingBackdrop open={loading} />
 * <LoadingBackdrop open={loading} spinnerColor="primary" />
 * <LoadingBackdrop open={open}>
 *   <Typography variant="h6">Processing...</Typography>
 * </LoadingBackdrop>
 * <LoadingBackdrop open={open} sx={{ zIndex: 9999 }} />
 * ```
 */
export const LoadingBackdrop = forwardRef<HTMLDivElement, LoadingBackdropProps>(
  (
    {
      open,
      children,
      showSpinner = true,
      spinnerSize = 40,
      spinnerColor = 'inherit',
      sx,
      'data-testid': dataTestId = 'loading-backdrop',
      ...other
    },
    ref
  ) => {
    const mergedSx: SxProps<Theme> = {
      ...defaultProgressSx,
      ...sx,
    };

    return (
      <MuiBackdrop ref={ref} data-testid={dataTestId} sx={mergedSx} open={open} {...other}>
        {children ??
          (showSpinner && (
            <CircularProgress
              data-testid={`${dataTestId}-spinner`}
              color={spinnerColor}
              size={spinnerSize}
            />
          ))}
      </MuiBackdrop>
    );
  }
);

LoadingBackdrop.displayName = 'LoadingBackdrop';
